import type { VoxelData } from '@/voxelTypes'

/**
 * A page palette from named groups, in order: the list for the document and the index of every
 * group, so a page never keeps a hand-counted `*_PALETTE_FIRST_*` offset.
 *
 *   const PALETTE = definePalette({ paper: PAPER, inks: INK_COLORS.map(solid) })
 *   createPageDocument(cells, { palette: PALETTE.list, ... })
 *   setDrawTool(store, PALETTE.at('inks', ink), radius)
 *
 * Indexes are stored in documents (a tool's palette index): append groups at the end, never
 * reorder a shipped page's groups.
 */
export type PageVoxelPalette<K extends string> = {
  /** Every entry, group after group (the document's `meta.palette`). */
  readonly list: VoxelData[]
  /** Index of each group's first entry. */
  readonly first: Readonly<Record<K, number>>
  /** Index of entry `i` (default 0) of `group`. */
  at(group: K, i?: number): number
}

export function definePalette<K extends string>(groups: Record<K, VoxelData | readonly VoxelData[]>): PageVoxelPalette<K> {
  const list: VoxelData[] = []
  const first = {} as Record<K, number>
  for (const name of Object.keys(groups) as K[]) {
    const g = groups[name]
    const items = Array.isArray(g) ? (g as readonly VoxelData[]) : [g as VoxelData]
    first[name] = list.length
    list.push(...items)
  }
  return {
    list,
    first,
    at: (group, i = 0) => first[group] + i,
  }
}
